<template>
    <v-dialog :value="value" max-width="900" persistent @input="$emit('input', $event)">
        <v-card class="ui-card rma-email">
            <div class="ui-card__header rma-email__header">
                <div>
                    <h3 class="ui-section-title">{{ preview ? `${preview.type_label} · ${competence.label}` : "E-mail da fiscalização mensal" }}</h3>
                    <p class="ui-section-description">Confira o e-mail e edite o que precisar antes de enviar, por exemplo para incluir um agendamento de reunião.</p>
                </div>
            </div>

            <div class="rma-email__body">
                <div v-if="loading" class="ui-small ui-muted py-10 text-center">Montando o e-mail...</div>
                <template v-else-if="preview">
                    <div v-if="preview.draft" class="ui-suggestion ui-suggestion--warn mb-3">
                        <strong>Rascunho {{ preview.draft.origin === "system" ? "preparado automaticamente" : "salvo" }}</strong>
                        em {{ formatDateTime(preview.draft.updated_at || preview.draft.created_at) }}. Ainda não foi enviado à devedora.
                    </div>

                    <div class="rma-email__meta">
                        <div class="rma-email__row">
                            <span class="rma-email__key">Para</span>
                            <div class="rma-email__recipients">
                                <span v-for="recipient in preview.recipients" :key="recipient.email" class="rma-email-chip" :title="recipient.name || ''">{{ recipient.email }}</span>
                                <span v-for="email in preview.copies" :key="`cc-${email}`" class="rma-email-chip rma-email-chip--copy" title="Cópia interna">{{ email }} · cópia</span>
                                <span v-if="!preview.recipients.length" class="rma-email__warn">Nenhum usuário da recuperanda cadastrado: o e-mail não terá destinatário.</span>
                            </div>
                        </div>
                        <div class="rma-email__row rma-email__row--subject">
                            <span class="rma-email__key">Assunto</span>
                            <input v-model="subject" type="text" maxlength="500" class="rma-email__subject" aria-label="Assunto" />
                        </div>
                    </div>

                    <div class="rma-email__hint">
                        <v-icon x-small>mdi-information-outline</v-icon>
                        <span>
                            A saudação ("Prezado(a) nome"), o botão do Painel da Devedora e o rodapé entram automaticamente.
                            <template v-if="preview.missing !== null"> A lista mostra os {{ preview.missing }} documento(s) pendente(s) agora.</template>
                        </span>
                        <v-spacer></v-spacer>
                        <v-btn v-if="$allowed('rma.request')" x-small text color="primary" :disabled="!isChanged" @click="restoreDefaults">Restaurar texto padrão</v-btn>
                    </div>

                    <tiptap-vuetify :key="editorKey" v-model="body" :extensions="extensions" class="rma-email-editor" @input="onBodyInput" />
                </template>
            </div>

            <div class="ui-card__footer rma-email__footer">
                <v-btn outlined :disabled="busy !== null" @click="$emit('input', false)">Cancelar</v-btn>
                <v-spacer></v-spacer>
                <v-btn v-if="$allowed('rma.request')" outlined :disabled="!preview || busy !== null" :loading="busy === 'draft'" @click="save('draft')">
                    <v-icon left small>mdi-content-save-outline</v-icon>Salvar rascunho e enviar depois
                </v-btn>
                <v-btn v-if="$allowed('rma.request')" color="primary" depressed :disabled="!preview || busy !== null" :loading="busy === 'send'" @click="save('send')">
                    <v-icon left small>mdi-email-fast-outline</v-icon>Enviar agora
                </v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from "axios";
import {
    TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HardBreak, HorizontalRule, History,
} from "tiptap-vuetify";
import { formatDateTime, notifyError, notifySuccess, notifyWarning } from "@/components/rma/rma.util";

// Editor dos e-mails da regua (solicitacao, lembrete, cobranca): previa com o texto
// padrao ou o rascunho pendente, edicao e envio (ou rascunho para enviar depois).
export default {
    name: "RmaEmailDialog",

    components: { TiptapVuetify },

    props: {
        value: { type: Boolean, default: false },
        competence: { type: Object, required: true },
        // Tipo pedido (rascunho pendente); sem tipo, a API escolhe o proximo e-mail da regua.
        type: { type: String, default: null },
    },

    data: () => ({
        loading: false,
        busy: null,
        preview: null,
        subject: "",
        body: "",
        original: "",
        edited: false,
        ready: false,
        editorKey: 0,
        extensions: [
            History, Blockquote, Link, Underline, Strike, Italic, ListItem, BulletList, OrderedList,
            [Heading, { options: { levels: [2, 3] } }], Bold, HorizontalRule, Paragraph, HardBreak,
        ],
    }),

    computed: {
        isChanged() {
            return !!this.preview && (this.edited || this.subject !== this.preview.defaults.subject || this.original !== this.preview.defaults.body);
        },
    },

    watch: {
        value(open) {
            if (open) this.load();
        },
    },

    methods: {
        formatDateTime,

        async load() {
            this.loading = true;
            this.preview = null;
            try {
                const result = await axios.get(`/api/admin/rma/competences/${this.competence.id}/email`, { params: this.type ? { type: this.type } : {} });
                this.applyContent(result.data.content, result.data.content.subject, result.data.content.body);
            } catch (error) {
                notifyError(this, error);
                this.$emit("input", false);
            } finally {
                this.loading = false;
            }
        },

        // O editor normaliza o HTML ao carregar: so conta como edicao o que vier depois.
        applyContent(preview, subject, body) {
            this.preview = preview;
            this.subject = subject;
            this.body = body;
            this.original = body;
            this.edited = false;
            this.ready = false;
            this.editorKey += 1;
            setTimeout(() => { this.ready = true; }, 400);
        },

        onBodyInput() {
            if (this.ready) this.edited = true;
        },

        restoreDefaults() {
            this.applyContent(this.preview, this.preview.defaults.subject, this.preview.defaults.body);
        },

        async save(action) {
            this.busy = action;
            try {
                const result = await axios.post(`/api/admin/rma/competences/${this.competence.id}/email`, {
                    type: this.preview.type,
                    subject: this.subject,
                    body: this.edited ? this.body : this.original,
                    action,
                });
                const content = result.data.content;
                if (action === "draft") {
                    notifySuccess(this, "Rascunho salvo. Ele fica no Histórico até ser enviado ou descartado.");
                } else {
                    const outcome = content.result || {};
                    if (outcome.no_recipients) notifyWarning(this, "Não há usuário da recuperanda cadastrado para receber o e-mail.");
                    else if (!outcome.sent) notifyWarning(this, "O e-mail não foi entregue a nenhum destinatário. Veja a situação na lista de e-mails.");
                    else notifySuccess(this, "E-mail enviado e registrado no histórico.");
                }
                this.$emit("saved", content);
                this.$emit("input", false);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = null;
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import "../../assets/scss/ui/tokens";

// Uma rolagem so (o corpo): cabecalho e botoes ficam fixos.
.rma-email {
    display: flex;
    flex-direction: column;
    // O v-dialog limita a 90% da tela: o card cabe dentro, sem cortar os botoes.
    max-height: calc(90vh - 2px);
    overflow: hidden;
}

.rma-email__header,
.rma-email__footer {
    flex: none;
}

.rma-email__header {
    border-bottom: 1px solid $ui-border-soft;
}

.rma-email__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 20px 20px;
}

.rma-email__footer {
    flex-wrap: wrap;
}

.rma-email__meta {
    border: 1px solid $ui-border;
    border-radius: 10px;
    margin-bottom: 12px;
}

.rma-email__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 14px;
    min-height: 44px;

    & + & {
        border-top: 1px solid $ui-border-soft;
    }
}

.rma-email__key {
    flex: none;
    width: 64px;
    font-size: 12.5px;
    color: $ui-muted;
}

.rma-email__recipients {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
}

.rma-email__warn {
    font-size: 12.5px;
    color: #b42318;
}

.rma-email__subject {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    color: $ui-fg;
    background: transparent;
}

.rma-email__row--subject:focus-within {
    box-shadow: inset 0 -2px 0 $ui-accent;
}

.rma-email__hint {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: $ui-muted;
    margin-bottom: 8px;
}

.rma-email-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    background: #f1effa;
    color: #2b2050;
    font-size: 12.5px;

    &--copy {
        background: #eef0f3;
        color: #555;
    }
}

// Editor: barra de ferramentas fixa enquanto o texto rola; texto no tamanho do e-mail.
.rma-email-editor {
    ::v-deep .v-card {
        border: 1px solid $ui-border;
        border-radius: 10px !important;
        box-shadow: none !important;
        overflow: visible;
    }

    ::v-deep .tiptap-vuetify-editor__toolbar {
        position: sticky;
        top: -16px;
        z-index: 2;
        background: $ui-bg-muted;
        border-bottom: 1px solid $ui-border-soft;
        border-radius: 10px 10px 0 0;
    }

    ::v-deep .tiptap-vuetify-editor__content {
        min-height: 300px;
        padding: 0 !important;
    }

    ::v-deep .ProseMirror {
        min-height: 280px;
        margin: 0 !important;
        padding: 14px 18px !important;
        font-size: 14px;
        line-height: 1.55;
        text-align: left;
        color: $ui-fg;
        outline: none;

        p {
            margin: 0 0 10px !important;
            text-align: left !important;
        }

        ul,
        ol {
            margin: 0 0 10px !important;
            padding-left: 22px !important;
        }

        li {
            margin: 2px 0 !important;

            p {
                margin: 0 !important;
            }
        }
    }
}

@media (max-width: 600px) {
    .rma-email__footer .v-btn {
        flex: 1 1 100%;
    }
}
</style>
